import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { SelectItem } from '@mydocma/dynamic-form/lib/fields/filter-select/select-item';
import { MmViewService } from '../../mm-view.service';
import { MmDbService } from '../../mm-db/mm-db-service';
import { MmProjectDbCollections } from '../../mm-db/mm-project-db-schema';
import { Delay } from '@docma-mm-core/delay/delay';
import { DelayService } from '@docma-mm-core/delay/delay.service';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY_FILTER_ID } from './field-mapping.enum';
import { Logger } from '@mydocma/common/src/lib/logger';

@Injectable()
export class FieldsDelayFilterService implements OnDestroy {
  
  private currentProjectId: number;
  private delaysSubject = new BehaviorSubject<SelectItem[]>([]);
  private destroy$ = new Subject<void>();
  
  public delays$ = this.delaysSubject.asObservable();
  
  constructor(
    private readonly mmViewService: MmViewService,
    private readonly mmDbService: MmDbService,
    private readonly translateService: TranslateService,
    private readonly delayService: DelayService,
    private readonly logger: Logger
  ) {
    this.initialize();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initialize(): void {
    this.mmViewService.currentProject$
      .pipe(
        filter(project => !!project),
        takeUntil(this.destroy$)
      )
      .subscribe(project => {
        this.currentProjectId = project.Id;
        this.loadDelays(project.MyDocmaUid);
      });
  }

  private async loadDelays(projectMyDocmaUid: string): Promise<void> {
    try {
      const db = await this.mmDbService.getProjectDb(projectMyDocmaUid);
      const delays = await db.getAll(MmProjectDbCollections.delays as never) as Delay[];
      const selectItems = this.formatDelaysForDropdown(delays || []);
      this.delaysSubject.next(selectItems);
    } catch (error) {
      this.logger.error(error, '', 'FieldsDelayFilterService');
      this.delaysSubject.next([]);
    }
  }

  private formatDelaysForDropdown(delays: Delay[]): SelectItem[] {
    const selectItems: SelectItem[] = [];
    
    selectItems.push({
      id: EMPTY_FILTER_ID,
      text: this.translateService.instant('MM.fields.filter.empty')
    });
    
    const sortedDelays = delays
      .filter(delay => delay.Id)
      .sort((a, b) => (a.Name1 || '').localeCompare(b.Name1 || ''));
    
    sortedDelays.forEach(delay => {
      const name1 = delay.Name1 || '';
      const name2 = delay.Name2 || '';
      const displayText = name1 && name2 ? `${name1} - ${name2}` : (name1 || name2 || '');
      
      selectItems.push({
        id: delay.Id.toString(),
        text: displayText,
        data: {
          delayImage: delay.DelayImage
        }
      });
    });
    
    return selectItems;
  }


  public getDelayDisplayNameById(delayId: string): string {
    const delays = this.delaysSubject.value;
    const delay = delays.find(d => d.id === delayId);
    return delay?.text || delayId;
  }
}